import { useEffect, useState } from 'react';
import { generatePath } from 'react-router-dom';
import classNames from 'classnames';
import { t } from 'ttag';

import StarIcon from '@svg/saved-star.svg';
import { brandFeed } from '@legacyUrls';
import { BRAND_PAGE_REFERRER, subsProductsBrandPopupKey } from '@constants';
import { useAppContext } from '@contexts/AppContext';
import { setBrandPageReferrerToStorage } from '@utils/common';
import { Tooltip } from '@ui/Tooltip';
import { Button } from '@ui/Button';
import { Link } from '@ui/Link';

import s from './ProductDetailsBrand.sss';

interface Brand {
  id: number,
  name: string,
  slug?: string | null,
  isViewerSubscribed?: boolean | null,
}

interface Props {
  className?: string,
  brand: Brand,
  isSubscriptionLoading: boolean,
  onToggleSubscription: () => void,
}

export const ProductDetailsBrand: React.FC<Props> = ({
  className,
  brand,
  isSubscriptionLoading,
  onToggleSubscription,
}) => {
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  const { isOutlet } = useAppContext();

  const handleBrandClick = () => {
    setBrandPageReferrerToStorage(brand.name, BRAND_PAGE_REFERRER.PRODUCT);
  };

  useEffect(() => {
    const viewedBrands = JSON.parse(
      sessionStorage.getItem(subsProductsBrandPopupKey) || '[]',
    );

    if (!viewedBrands.includes(brand.id)) {
      setIsTooltipOpen(true);
    }

    sessionStorage.setItem(
      subsProductsBrandPopupKey,
      JSON.stringify([...viewedBrands, brand.id]),
    );
  }, [brand.id]);

  if (isOutlet) {
    return (
      <p className={classNames(s.brand, className)}>
        {brand.name}
      </p>
    );
  }

  return (
    <div className={classNames(s.root, className)}>
      <Link
        className={s.brand}
        theme='black'
        to={generatePath(brandFeed, { slug: brand.slug || '' })}
        onClick={handleBrandClick}
      >
        {brand.name}
      </Link>

      <Tooltip
        isVisible={isTooltipOpen}
        isCloseOnOutsideClick={false}
        isInteractive={true}
        content={(
          <p className={s.tooltip}>
            {t`Вам цікаві речі цього бренду? Підпишіться на нього, щоб дізнаватися про новинки`}

            <Button
              className={s.tooltipButton}
              theme='text'
              onClick={() => setIsTooltipOpen(false)}
            >
              {t`Добре`}
            </Button>
          </p>
        )}
        placement="top"
        theme="black"
        padding="16px"
        width="260px"
      >
        <span className={s.iconWrapper}>
          <StarIcon
            className={classNames(s.icon, {
              [s.iconStateFilled]: brand.isViewerSubscribed,
            })}
            width='16'
            height='16'
          />
        </span>
      </Tooltip>

      <Button
        className={s.button}
        theme='text'
        onClick={onToggleSubscription}
        disabled={isSubscriptionLoading}
      >
        {brand.isViewerSubscribed
          ? t`Відписатися від бренду`
          : t`Підписатися на бренд`}
      </Button>
    </div>
  );
};
